import React from 'react';
import { Box, Typography } from '@mui/material';
import { Show, useShowContext } from 'framework/show';
import { generateLinkByEmail } from 'framework/utils';
import ContactSection from '../shared/ContactSection';
import { EmployeePersonTile } from 'domain/shared/core/PersonTile';
import { READ_EMPLOYEE_PERMISSION, useEmployeePermissionsMap } from 'domain/shared/employee';
import useContactsEmployeeService from '../shared/services/contactsEmployeeService';
import { ManagerData } from '../shared/types';

const HRManagersList = () => {
  const { data } = useShowContext();
  const hrManagers = data?.getData() || [];

  const aliases = hrManagers.map((m: ManagerData) => m.alias);
  const { permissionsMap } = useEmployeePermissionsMap(aliases);

  if (hrManagers.length === 0) {
    return <Typography color="textScondary">No HR managers found</Typography>;
  }

  return (
    <Box>
      {hrManagers.map((manager: ManagerData) => {
        const isLink =
          Array.isArray(permissionsMap[manager.alias]) &&
          permissionsMap[manager.alias].includes(READ_EMPLOYEE_PERMISSION);

        return (
          <Box key={manager.alias} sx={{ mb: 2 }}>
            <EmployeePersonTile
              employeeId={manager.alias}
              title="HR manager"
              size="medium"
              isLink={isLink}
              showTeamsButton
              teamsIconSize="medium"
              teamsUrl={manager?.workingEmail && generateLinkByEmail(manager.workingEmail)}
            />
          </Box>
        );
      })}
    </Box>
  );
};

const HRManagementSection: React.FC = () => {
  const { searchHRManagers } = useContactsEmployeeService();

  const descriptionItems = [
    {
      title: 'Psychological support & wellbeing',
      subtitle: 'stress, tension, emotional concerns',
    },
    {
      title: 'Workplace complaints / violations',
      subtitle: 'conflict, misconduct, any situation, requiring HR intervention',
    },
  ];

  const description = (
    <Box>
      {descriptionItems.map(item => (
        <Box key={item.title} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'success.main',
                mr: 1.5,
              }}
            />
            <Typography variant="body2">{item.title}</Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" ml={2.3}>
            {item.subtitle}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <ContactSection title="HR Management" subtitle="Issues HR manager can help with" description={description}>
      <Show getDataMethod={searchHRManagers} isSmallLoader>
        <HRManagersList />
      </Show>
    </ContactSection>
  );
};

export default HRManagementSection;
